import classNames from 'classnames'
import { navigateToAndResetPageAndSearch } from 'front-end/src/pages/analytics/helpers'
import React, { FC, useContext, useState } from 'react'
import { truncate } from 'lodash'
import { Box, Flex } from 'front-end/src/components'
import { Column } from 'react-table'
import { t } from '@lingui/macro'
import {
  AnalyticsCertificationsFilter,
  CertificationsOverviewOrderColumn,
  SqlOrderDirectionEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { brandColors } from 'shared/basicPalette'
import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from '../../../components/common/table-extended-old/TableExtendedOld'
import { getPagesCount } from '../utils'
import { countWidthRate } from '../../surveys/helpers'
import { DownloadCertificationsCSV } from './DownloadCertificationsCSV'
import { linkBuilder } from 'shared/linkBuilder'
import './CertificationsOverviewTable.less'

import { useCertificationsOverviewQuery } from './CertificationsOverviewTable.codegen'
import { AnalyticsContext } from '../AnalyticsContext'
import { StatusArchivedGrey } from 'front-end/src/components/common/status/Status'

export const getProgressText = (completed, total) => {
  return `${countWidthRate(completed, total)} ${t`completed`}`
}

const CertificationsOverviewTable: FC<{}> = () => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const [overviewTableFilters, setOverviewTableFilters] = useState<
    NonNullable<AnalyticsCertificationsFilter>
  >({
    limit: 20,
    orderColumn: CertificationsOverviewOrderColumn.name,
    orderDirection: SqlOrderDirectionEnum.ASC
  })

  const overviewTableQuery = {
    ...overviewTableFilters,
    page: filter.page,
    search: filter.search
  }

  const { data, loading } = useCertificationsOverviewQuery({
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      certificationsOverviewFilter: overviewTableQuery
    }
  })

  const limit = overviewTableQuery.limit || 20

  const tableColumns: Array<Column<any>> = [
    {
      Header: t`Name`,
      accessor: 'name',
      id: CertificationsOverviewOrderColumn.name,
      Cell: (row) => {
        return (
          <div className="overflowEllipsis">
            <strong className="dark">
              {truncate(row.value, { length: 120 })}
              {row.original.deleted_at && <StatusArchivedGrey mx="8px" />}
            </strong>
          </div>
        )
      }
    },
    {
      Header: t`Completed`,
      maxWidth: 140,
      width: 100,
      accessor: 'completed_count',
      id: CertificationsOverviewOrderColumn.completed_count,
      Cell: (row) => <span className="green">{row.value}</span>
    },
    {
      Header: t`Started`,
      maxWidth: 140,
      width: 100,
      accessor: 'in_progress_count',
      id: CertificationsOverviewOrderColumn.in_progress_count,
      Cell: (row) => <span className="blue">{row.value}</span>
    },
    {
      Header: t`Shared`,
      maxWidth: 140,
      width: 100,
      accessor: 'shared_count',
      id: CertificationsOverviewOrderColumn.shared_count,
      Cell: (row) => <span className="grey">{row.value}</span>
    },
    {
      Header: t`Progress`,
      sortable: false,
      accessor: '',
      maxWidth: 140,
      Cell: (row) => {
        const completedStyles = {
          width: `${countWidthRate(
            row.original.completed_count,
            row.original.shared_count
          )}`
        }

        const inProgressStyles = {
          width: `${countWidthRate(
            row.original.in_progress_count + row.original.completed_count,
            row.original.shared_count
          )}`
        }

        return (
          <Flex direction="column" mr="10px">
            <Box
              mb="2px"
              textAlign="center"
              fontWeight="bold"
              fontSize="14px"
              color={
                row.original.completed_count
                  ? brandColors.green['600']
                  : brandColors.grey['300']
              }
            >
              {getProgressText(
                row.original.completed_count,
                row.original.shared_count
              )}
            </Box>
            <Box className="ResultBar">
              <span
                className="ResultBar__inProgress"
                style={inProgressStyles}
              />
              <span className="ResultBar__completed" style={completedStyles} />
            </Box>
          </Flex>
        )
      }
    }
  ]

  let pagesCount = 1
  if (data && data.analytics) {
    pagesCount = getPagesCount(
      data.analytics.certifications?.overview?.count || 0,
      limit
    )
  }

  return (
    <div>
      <TableExtendedOld
        loading={loading}
        manual
        data={data?.analytics.certifications?.overview?.items}
        tableHeader={t`Certificates`}
        columns={tableColumns}
        className="-highlight AnalyticsTable AnalyticsTable__certifications"
        defaultPageSize={limit}
        showPageSizeOptions={false}
        showSearchFilter
        resizable={false}
        minRows={1}
        pages={pagesCount}
        filterFields={['name']}
        getTrProps={(_state, row) => ({
          onClick: () =>
            navigateToAndResetPageAndSearch(
              linkBuilder.analyticsCertificationDetail(row.original.id),
              changeFilter
            ),
          className: classNames({
            archived: row && row.original.deleted_at,
            clickable: true
          }),
          ...DEFAULT_TR_PROPS
        })}
        paginationContentAfter={
          data &&
          data.analytics?.certifications && (
            <DownloadCertificationsCSV
              certificationsOverviewFilter={overviewTableQuery}
              certificationsCount={
                data.analytics.certifications?.overview?.count || 0
              }
            />
          )
        }
        defaultSortDesc
        defaultSorted={[
          {
            id: overviewTableQuery.orderColumn as string,
            desc:
              overviewTableQuery.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          setOverviewTableFilters(
            (prevState: NonNullable<AnalyticsCertificationsFilter>) => {
              const descendingOrder = newSorted[0] ? newSorted[0].desc : false

              return {
                ...prevState,
                orderColumn: newSorted[0]
                  ? (newSorted[0].id as CertificationsOverviewOrderColumn)
                  : CertificationsOverviewOrderColumn.name,
                orderDirection: descendingOrder
                  ? SqlOrderDirectionEnum.DESC
                  : SqlOrderDirectionEnum.ASC
              }
            }
          )
        }}
        onPageChange={(newPage) => {
          changeFilter({ page: newPage + 1 })
        }}
        defaultSearch={overviewTableQuery.search}
        page={overviewTableQuery.page - 1}
        onSearchChange={(searchVal) => {
          changeFilter({ search: searchVal })
        }}
      />
    </div>
  )
}

export default CertificationsOverviewTable
